/**
 * SMS Inbox quick-reply template manager.
 *
 * Renders inside Settings so admins can add, rename, reorder, and archive
 * one-tap replies without touching the DB. The Inbox composer picks them
 * up on next mount.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Plus, Pencil, Trash2, GripVertical, Archive, ArchiveRestore, MessageSquare, Zap } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function SmsTemplatesCard() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // template object or "new"
  const [form, setForm] = useState({ title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      // The public /sms/templates endpoint filters out archived rows to keep
      // the Inbox chip row clean. For Settings we ask the backend for ALL
      // templates (including archived) via `include_archived=true`.
      const { data } = await axios.get(`${API_URL}/sms/templates?include_archived=true`, {
        headers: auth(),
      });
      setTemplates(data.templates || []);
    } catch (err) {
      toast.error("Couldn't load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleTemplates = templates
    .filter((t) => (showArchived ? true : t.enabled !== false))
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || (a.title || "").localeCompare(b.title || ""));

  const openNew = () => {
    setForm({ title: "", body: "" });
    setEditing("new");
  };

  const openEdit = (tpl) => {
    setForm({ title: tpl.title || "", body: tpl.body || "" });
    setEditing(tpl);
  };

  const close = () => {
    setEditing(null);
    setForm({ title: "", body: "" });
  };

  const save = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || !body) {
      toast.error("Title and body are required");
      return;
    }
    if (body.length > 320) {
      toast.error("Body is longer than 320 characters — texts should be short");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const order = (templates.reduce((m, t) => Math.max(m, t.order ?? 0), 0) + 1);
        await axios.post(`${API_URL}/sms/templates`, { title, body, order, enabled: true }, { headers: auth() });
        toast.success("Template added");
      } else {
        await axios.put(`${API_URL}/sms/templates/${editing.id}`, {
          title, body, order: editing.order, enabled: editing.enabled,
        }, { headers: auth() });
        toast.success("Template updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/sms/templates/${tpl.id}`, { headers: auth() });
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleArchive = async (tpl) => {
    try {
      await axios.put(`${API_URL}/sms/templates/${tpl.id}`, {
        title: tpl.title, body: tpl.body, order: tpl.order, enabled: !tpl.enabled,
      }, { headers: auth() });
      toast.success(tpl.enabled ? "Archived" : "Restored");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const moveTemplate = async (fromIndex, toIndex) => {
    // Optimistically reorder in-memory then persist new `order` values to
    // every affected template. Simplest reliable approach: reassign 1..N
    // across the whole visible list based on the new position.
    if (fromIndex === toIndex) return;
    const visible = visibleTemplates.slice();
    const [moved] = visible.splice(fromIndex, 1);
    visible.splice(toIndex, 0, moved);
    // Assign fresh sequential order values.
    const updates = visible.map((t, idx) => ({ ...t, order: idx + 1 }));
    // Optimistic UI: merge new orders into local state.
    setTemplates((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      updates.forEach((t) => byId.set(t.id, { ...byId.get(t.id), order: t.order }));
      return Array.from(byId.values());
    });
    try {
      await Promise.all(updates.map((t) => axios.put(
        `${API_URL}/sms/templates/${t.id}`,
        { title: t.title, body: t.body, order: t.order, enabled: t.enabled },
        { headers: auth() },
      )));
    } catch {
      toast.error("Reorder failed");
      load();  // roll back to server state
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    moveTemplate(result.source.index, result.destination.index);
  };

  return (
    <>
      <Card data-testid="sms-templates-card">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#0F4C81]" />
              Inbox Quick Replies
            </CardTitle>
            <CardDescription>
              One-tap chips above the Inbox composer. Tap fills the draft — never auto-sends.
            </CardDescription>
          </div>
          <Button
            onClick={openNew}
            size="sm"
            className="bg-[#0F4C81] hover:bg-[#0F4C81]/90 flex-shrink-0"
            data-testid="sms-template-add-btn"
          >
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Add template</span>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Switch
              checked={showArchived}
              onCheckedChange={setShowArchived}
              id="show-archived"
              data-testid="sms-template-show-archived"
            />
            <Label htmlFor="show-archived" className="text-sm text-slate-600">Show archived</Label>
          </div>

          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
          ) : visibleTemplates.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-lg">
              <MessageSquare className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              No templates yet. Add your first quick reply.
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sms-templates">
                {(dropProvided) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden"
                  >
                    {visibleTemplates.map((tpl, i) => {
                      const isArchived = tpl.enabled === false;
                      return (
                        <Draggable key={tpl.id} draggableId={tpl.id} index={i}>
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 ${isArchived ? "bg-slate-50/60" : "bg-white"} ${snapshot.isDragging ? "shadow-lg ring-2 ring-[#0F4C81]/30" : ""}`}
                              data-testid={`sms-template-row-${tpl.id}`}
                              style={{ ...dragProvided.draggableProps.style }}
                            >
                              <button
                                type="button"
                                {...dragProvided.dragHandleProps}
                                className="flex-shrink-0 h-11 w-8 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing"
                                aria-label="Drag to reorder"
                                data-testid={`sms-template-drag-${tpl.id}`}
                              >
                                <GripVertical className="w-4 h-4" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`text-sm font-semibold truncate ${isArchived ? "text-slate-400 line-through" : "text-slate-800"}`}>
                                    {tpl.title}
                                  </p>
                                  {isArchived && (
                                    <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                                      Archived
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{tpl.body}</p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEdit(tpl)}
                                  className="h-11 w-11 sm:h-9 sm:w-auto sm:px-3 p-0"
                                  data-testid={`sms-template-edit-${tpl.id}`}
                                  aria-label="Edit template"
                                >
                                  <Pencil className="w-4 h-4 sm:mr-1.5" />
                                  <span className="hidden sm:inline">Edit</span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleArchive(tpl)}
                                  className="h-11 w-11 sm:h-9 sm:w-auto sm:px-3 p-0 text-slate-500 hover:text-slate-800"
                                  data-testid={`sms-template-archive-${tpl.id}`}
                                  aria-label={isArchived ? "Restore template" : "Archive template"}
                                >
                                  {isArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => remove(tpl)}
                                  className="h-11 w-11 sm:h-9 sm:w-auto sm:px-3 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                  data-testid={`sms-template-delete-${tpl.id}`}
                                  aria-label="Delete template"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-lg" data-testid="sms-template-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New quick reply" : "Edit quick reply"}
            </DialogTitle>
            <DialogDescription>
              Short title for the chip. The body fills the composer when tapped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="tpl-title">Chip label</Label>
              <Input
                id="tpl-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Running late"
                maxLength={30}
                data-testid="sms-template-title-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{form.title.length}/30 · Short is better for the chip row.</p>
            </div>
            <div>
              <Label htmlFor="tpl-body">Message body</Label>
              <Textarea
                id="tpl-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Hey — running about 15 minutes late. Sorry about that!"
                rows={4}
                maxLength={320}
                data-testid="sms-template-body-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{form.body.length}/320 · One SMS segment is 160 characters.</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={close} disabled={saving} data-testid="sms-template-cancel-btn">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !form.title.trim() || !form.body.trim()}
              className="bg-[#0F4C81] hover:bg-[#0F4C81]/90"
              data-testid="sms-template-save-btn"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
